import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addCardProps } from '../../actions';
import { IState } from '../../graphql/types';
import { SliderPicker } from 'react-color';
import PropTypes from 'prop-types';

import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertFromRaw } from 'draft-js';

import styles from './CardForm.module.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { Popover } from 'antd';
import { FontColorsOutlined } from '@ant-design/icons';

class ColorPic extends React.Component<Record<string, any>, { open: boolean }> {
  static propTypes = {
    expanded: PropTypes.bool,
    onExpandEvent: PropTypes.func,
    onChange: PropTypes.func,
    currentState: PropTypes.object,
  };

  stopPropagation = event => {
    event.stopPropagation();
  };

  constructor(props) {
    super(props);
    this.state = {
      open: false,
    };
  }

  onChange = color => {
    if (color.hex) {
      const { onChange } = this.props as any;
      onChange('color', color.hex);
    }
  };

  toggleOpen = () => {
    this.setState(prevState => ({ open: !prevState.open }));
  };

  render() {
    const { open } = this.state;
    const { color } = (this.props as any).currentState;

    return (
      <div
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="rdw-color-picker"
      >
        <Popover
          overlayClassName={styles.colorPickerPopover}
          content={
            <SliderPicker
              color={color ?? '#000000'}
              onChangeComplete={this.onChange}
            />
          }
          trigger="click"
        >
          <div
            className={`rdw-option-wrapper ${open ? 'rdw-option-active' : ''}`}
            style={{ width: '34px' }}
          >
            <FontColorsOutlined />
          </div>
        </Popover>
      </div>
    );
  }
}

const toolbarOptions = {
  options: [
    'inline',
    'blockType',
    'list',
    'colorPicker',
    'remove',
    'fontSize',
    'fontFamily',
  ],
  inline: {
    inDropdown: false,
    options: ['bold', 'italic', 'underline'],
  },
  blockType: {
    inDropdown: false,
    options: ['Normal', 'H1', 'H2', 'Blockquote'],
  },
  colorPicker: { component: ColorPic },
  list: {
    inDropdown: false,
    className: undefined,
    component: undefined,
    dropdownClassName: undefined,
    options: ['unordered', 'ordered'],
  },
  fontSize: {
    options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
  },
  fontFamily: {
    options: [
      'Arial',
      'Georgia',
      'Impact',
      'Tahoma',
      'Times New Roman',
      'Verdana',
    ],
    className: 'underlined-select',
  },
};

const maxLength = 350;

const TextForm: React.FC<{ saving: boolean }> = ({ saving }) => {
  const [editorRef, setEditorRef] = useState<HTMLElement | null>(null);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const contentState = useSelector<IState, EditorState | string | undefined>(
    state => {
      if (
        state.cardProps?.htmlText &&
        typeof state.cardProps?.htmlText === 'string'
      ) {
        const contentState = convertFromRaw(
          JSON.parse(state.cardProps?.htmlText)
        );
        return EditorState.createWithContent(contentState);
      }
      return state.cardProps?.htmlText;
    }
  );

  const textLength =
    contentState?.getCurrentContent().getPlainText().length || 0;

  const handleSetEditorRef = ref => {
    ref?.focus();

    setEditorRef(ref);
  };

  const handleReturn = () => {};

  const handleEditorStateChange = useCallback(
    (currentState: EditorState) => {
      dispatch(addCardProps('htmlText', currentState));
    },
    [dispatch]
  );

  const handleBeforeInput = useCallback(
    (currentState: EditorState) => {
      // disable editor while saving card
      if (saving) return 'handled';
      if (currentState && textLength >= maxLength) {
        return 'handled';
      }
    },
    [saving, textLength]
  );

  const handlePastedText = useCallback(
    (pasteInput: string) => {
      return pasteInput.length + textLength >= maxLength;
    },
    [textLength]
  );

  const scrollToCursor = key => {
    // ensure cursor is always scrolled in to view when new line is added
    if (key.code !== 'Enter') {
      return;
    }

    const selectionState = contentState.getSelection();
    const focusedBlockKey = selectionState.getStartKey();

    // handle start key is undefined when pressing enter after a <ul> block
    if (!focusedBlockKey) {
      return;
    }

    const blocksWrapper = editorRef!.lastChild as HTMLElement; // direct child of the element with class public-DraftEditor-content
    const scrollableContainer = editorRef!.parentElement!.parentElement!;
    const blocks = [...blocksWrapper.children] as HTMLElement[];

    const focusedBlockIndex: number = blocks.findIndex((element: any) =>
      element.dataset.offsetKey.startsWith(focusedBlockKey)
    );

    const focusedBlock: HTMLElement = blocks[focusedBlockIndex];

    if (!focusedBlock) {
      return;
    }

    // if it's the first block, make sure it's scroll all the way to the top
    if (focusedBlockIndex === 0) {
      scrollableContainer.scrollTop = 0;
    }

    const blockLineHeight = focusedBlock.getBoundingClientRect().height;
    const focusedBlockFirstLineBottom =
      focusedBlock.offsetTop + blockLineHeight;

    const maxVisiblePosition =
      scrollableContainer.scrollTop + scrollableContainer.clientHeight;

    const diff = focusedBlockFirstLineBottom - maxVisiblePosition;

    if (diff > 0) {
      scrollableContainer.scrollTop += diff + 32;
    }
  };

  const getEditorClassList = useCallback(() => {
    return editorRef?.parentElement?.parentElement?.parentElement?.classList;
  }, [editorRef]);

  const handleFocusEditor = () => {
    getEditorClassList()?.add(styles.textEditorContentFocused);
  };

  const handleBlurEditor = () => {
    getEditorClassList()?.remove(styles.textEditorContentFocused);
  };

  useEffect(() => {
    getEditorClassList()?.add(styles.textEditorContentFocused);
  }, [getEditorClassList]);

  return (
    <>
      <div
        ref={containerRef}
        className={'feed-card'}
        style={{ visibility: 'hidden', position: 'absolute', zIndex: -9 }}
      />
      <div onKeyUp={scrollToCursor}>
        <Editor
          editorClassName={`${styles.textEditorContent}`}
          toolbarClassName={styles.textEditorToolbar}
          wrapperClassName={styles.textEditorWrapper}
          editorState={contentState}
          onEditorStateChange={handleEditorStateChange}
          handleBeforeInput={handleBeforeInput}
          editorRef={handleSetEditorRef}
          handleReturn={handleReturn}
          handlePastedText={handlePastedText}
          onBlur={handleBlurEditor}
          onFocus={handleFocusEditor}
          placeholder={
            'Start crafting your content here. Remember, you can format your text using the toolbar above.'
          }
          toolbar={toolbarOptions}
        />
        <div className={styles.textEditorCharCount}>
          <span>{`${textLength} / ${maxLength}`}</span>
        </div>
      </div>
    </>
  );
};

export default TextForm;
